import { isLoaded } from "./memory.js";
import { initLighting, renderLighting } from "./lighting.js";
import { getRoomAt, getRoomVisibility } from "./rooms.js";
import { isMemoryUIOpen } from "../ui/memoryUI.js";

/**
 * Optic Sensors & Vision Interface
 */

// How long the screen takes to fade up from black once Vision reveals —
// a "screen booting" cross-fade rather than an instant cut.
const VISION_FADE_IN_SECONDS = 1.2;
// Humans get their own (longer) boot time before they can spot the player —
// starts when vision actually reveals (memory closed), not when the module is placed.
const HUMAN_DETECTION_GRACE_SECONDS = 2.5;

let wasVisionLoaded = false;
let visionBootAt = null;
/** Vision is in RAM but lighting stays black until memory UI closes. */
let visionRevealPending = false;

export function initVision() {
    wasVisionLoaded = false;
    visionBootAt = null;
    visionRevealPending = false;
    initLighting();
}

function beginVisionReveal() {
    visionBootAt = time();
    visionRevealPending = false;
}

export function updateVision() {
    const visionLoaded = isLoaded("core_vision") || isLoaded("story_owner");
    const memOpen = isMemoryUIOpen();

    if (visionLoaded && !wasVisionLoaded) {
        // Placing Vision while memory is open must not light the room yet —
        // the fade (and human grace) only start after the page closes.
        if (memOpen) {
            visionBootAt = null;
            visionRevealPending = true;
        } else {
            beginVisionReveal();
        }
    } else if (!visionLoaded) {
        visionBootAt = null;
        visionRevealPending = false;
    }

    if (visionLoaded && visionRevealPending && !memOpen) {
        beginVisionReveal();
    }

    wasVisionLoaded = visionLoaded;

    const visionFade = !visionLoaded || visionRevealPending || visionBootAt == null
        ? 0
        : Math.min(1, (time() - visionBootAt) / VISION_FADE_IN_SECONDS);

    const player = get("player")[0];

    let roomFog = null;
    if (player) {
        const current = getRoomAt(player.pos);
        roomFog = {
            visibility: getRoomVisibility(player.pos),
            currentRoomId: current ? current.id : null,
        };
    }

    renderLighting(visionLoaded && !visionRevealPending, roomFog, visionFade);
}

/** True while humans must not spot the player (blind, pending reveal, or post-boot grace). */
export function isHumanDetectionGraceActive() {
    // No Vision module — player is blind; don't run the alert sequence in the dark.
    if (!(isLoaded("core_vision") || isLoaded("story_owner"))) return true;
    // Vision is in RAM but lighting hasn't faded up yet (memory still open).
    if (visionRevealPending || visionBootAt == null) return true;
    return time() - visionBootAt < HUMAN_DETECTION_GRACE_SECONDS;
}
